import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag, PackageOpen, ShieldCheck, Info, Building2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { zl } from "@/lib/shop";
import { shopApi, useShopAuth } from "@/lib/shopAuth";
import { useB2bCart, setQty, removeLine, cartSubtotal, clearCart } from "@/lib/b2bCart";
import RedfoxLayout from "./RedfoxLayout";
import { Button } from "@/components/ui/button";

const ORDER_KEY = "empro_b2b_order_key";
const getOrderKey = () => { let k = localStorage.getItem(ORDER_KEY); if (!k) { k = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random()); localStorage.setItem(ORDER_KEY, k); } return k; };

export default function MyStoreCart() {
  const auth = useShopAuth();
  const navigate = useNavigate();
  const items = useB2bCart();
  const [store, setStore] = useState(null);
  const [checking, setChecking] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [billing, setBilling] = useState(null);       // { companies, default_customer_id, choice_required }
  const [billingId, setBillingId] = useState("");

  useEffect(() => {
    if (!auth?.ready || !auth.customer) return;
    const sid = items[0]?.store_id;
    if (!sid) return;
    shopApi.get("/b2b/my/stores").then((r) => setStore((r.data || []).find((x) => x.id === sid) || null)).catch(() => {});
  }, [auth?.ready, auth?.customer, items.length]);

  useEffect(() => {
    if (!store?.id) return;
    shopApi.get(`/b2b/my/stores/${store.id}/billing-options`).then((r) => {
      const b = r.data || {};
      setBilling(b);
      // Jedna firma → wybór automatyczny. Wiele firm → świadomy wybór klienta (bez auto-podstawienia domyślnej).
      if (!b.choice_required && (b.companies || []).length) setBillingId(b.companies[0].id);
      else setBillingId("");
    }).catch(() => setBilling(null));
  }, [store?.id]);

  if (auth?.ready && !auth.customer) { navigate("/sklep/moj-sklep"); return null; }
  const subtotal = cartSubtotal();

  const groupedItems = () => {
    const byKey = {};
    items.forEach((l) => {
      const k = `${l.product_id}|${l.color}|${l.variant_id || ""}`;
      byKey[k] = byKey[k] || { product_id: l.product_id, color: l.color, variant_id: l.variant_id || "", variant_name: l.variant_name || "", print_color: l.print_color || "", sizes: {} };
      byKey[k].sizes[l.size] = (byKey[k].sizes[l.size] || 0) + l.quantity;
    });
    return Object.values(byKey);
  };

  const validate = async () => {
    setChecking(true);
    try {
      const { data } = await shopApi.post("/b2b/my/cart/validate", { items: groupedItems() });
      toast.success(`Koszyk poprawny — ${data.lines.length} pozycji, razem ${zl(data.total_grosz)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Walidacja nie powiodła się");
    } finally { setChecking(false); }
  };

  const placeOrder = async () => {
    if (!store?.id || !items.length) return;
    if ((billing?.companies || []).length > 1 && !billingId) {
      toast.error("Wybierz firmę do faktury dla tego zamówienia.");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await shopApi.post("/b2b/my/orders", { store_id: store.id, idempotency_key: getOrderKey(), billing_customer_id: billingId, items: groupedItems() });
      clearCart();
      localStorage.removeItem(ORDER_KEY);
      toast.success(data.duplicate ? `Zamówienie już złożone: ${data.number}` : `Zamówienie złożone: ${data.number}`);
      navigate("/sklep/moj-sklep/zamowienia");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się złożyć zamówienia");
    } finally { setPlacing(false); }
  };

  const companies = billing?.companies || [];
  const chosenCompany = companies.find((x) => x.id === billingId) || null;
  const checkoutDisabled = placing || checking || (companies.length > 1 && !billingId);

  return (
    <RedfoxLayout store={store}>
      <div className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="font-black text-3xl uppercase tracking-tight mb-8">Koszyk</h1>
        {items.length === 0 ? (
          <div className="grid place-items-center py-24 text-neutral-500" data-testid="redfox-cart-empty">
            <ShoppingBag className="h-12 w-12 mb-4" />
            <p className="mb-4">Twój koszyk jest pusty.</p>
            <Link to="/sklep/moj-sklep"><Button className="bg-red-600 hover:bg-red-500 text-white font-semibold">Przejdź do kolekcji</Button></Link>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it, i) => (
              <div key={i} data-testid={`redfox-cart-item-${i}`} className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="h-20 w-20 shrink-0 rounded-lg bg-neutral-800 overflow-hidden">
                  {it.photo ? <img src={`${API}/files/${it.photo}`} alt="" className="h-full w-full object-contain" /> : <div className="grid h-full place-items-center text-neutral-500"><PackageOpen className="h-7 w-7" /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{it.name}</p>
                  <p className="text-xs text-neutral-400">{[it.color, it.variant_name || it.print_color, `rozm. ${it.size}`].filter(Boolean).join(" · ")}</p>
                  <p className="mt-1 text-sm font-bold">{zl(it.price_grosz)}</p>
                </div>
                <div className="flex items-center rounded-lg border border-white/15">
                  <button data-testid={`redfox-cart-dec-${i}`} onClick={() => setQty(i, it.quantity - 1)} className="h-9 w-9 hover:text-red-500">−</button>
                  <span className="w-8 text-center text-sm" data-testid={`redfox-cart-qty-${i}`}>{it.quantity}</span>
                  <button data-testid={`redfox-cart-inc-${i}`} onClick={() => setQty(i, it.quantity + 1)} className="h-9 w-9 hover:text-red-500">+</button>
                </div>
                <div className="w-24 text-right font-bold">{zl(it.price_grosz * it.quantity)}</div>
                <button data-testid={`redfox-cart-remove-${i}`} onClick={() => removeLine(i)} className="text-rose-400 hover:text-rose-300"><Trash2 className="h-5 w-5" /></button>
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-white/10 pt-5">
              <span className="text-neutral-400">Wartość produktów (netto)</span>
              <span className="text-2xl font-extrabold" data-testid="redfox-cart-subtotal">{zl(subtotal)}</span>
            </div>

            {companies.length > 0 && (
              <div className="rounded-xl border border-white/10 bg-white/5 p-4" data-testid="redfox-billing">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="h-4 w-4 text-red-400" />
                  <h2 className="font-bold uppercase tracking-wide text-sm">Firma do faktury</h2>
                </div>
                <p className="text-xs text-neutral-400 mb-3">
                  {companies.length > 1
                    ? "Wybierz firmę, na którą ma zostać wystawiona faktura za to zamówienie."
                    : "Faktura zostanie wystawiona na poniższą firmę."}
                </p>
                <div className="space-y-2">
                  {companies.map((co) => {
                    const isDefault = co.id === billing?.default_customer_id;
                    const selected = co.id === billingId;
                    return (
                      <label key={co.id} data-testid={`redfox-billing-opt-${co.id}`}
                        className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${selected ? "border-red-500 bg-red-500/10" : "border-white/10 hover:bg-white/5"}`}>
                        <input type="radio" name="redfox-billing" data-testid={`redfox-billing-radio-${co.id}`}
                          className="mt-1 accent-red-600" checked={selected} onChange={() => setBillingId(co.id)} />
                        <div className="min-w-0">
                          <p className="font-semibold">{co.name}{isDefault && <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-medium text-neutral-300">sugerowana</span>}</p>
                          <p className="text-xs text-neutral-400">{[co.nip ? `NIP ${co.nip}` : "", co.address].filter(Boolean).join(" · ")}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                {companies.length > 1 && !billingId && (
                  <p className="mt-2 text-xs font-medium text-amber-300" data-testid="redfox-billing-required">Wymagany wybór firmy przed złożeniem zamówienia.</p>
                )}
                {chosenCompany && (
                  <p className="mt-3 text-xs text-neutral-300" data-testid="redfox-billing-chosen">
                    Faktura dla: <span className="font-semibold text-white">{chosenCompany.name}</span>{chosenCompany.nip ? ` (NIP ${chosenCompany.nip})` : ""}
                  </p>
                )}
              </div>
            )}

            <div className="rounded-xl bg-white/5 p-4 text-sm text-neutral-300 flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 shrink-0 text-neutral-400" />
              Bez płatności online. Faktura nie jest wystawiana automatycznie — EMPRO wystawi ją na wskazaną firmę. Warunki dostawy i rozliczenia ustala EMPRO poza sklepem.
            </div>
            <div className="flex flex-wrap justify-end gap-3 pt-2">
              <Link to="/sklep/moj-sklep"><Button variant="outline" className="border-white/20 text-neutral-200 hover:bg-white/10">Kontynuuj zakupy</Button></Link>
              <Button data-testid="redfox-cart-validate" onClick={validate} disabled={checking || placing} className="bg-white text-neutral-950 hover:bg-neutral-200 font-semibold gap-2">
                <ShieldCheck className="h-4 w-4" />{checking ? "Sprawdzanie…" : "Sprawdź dostępność"}
              </Button>
              <Button data-testid="redfox-cart-checkout" onClick={placeOrder} disabled={checkoutDisabled} className="bg-red-600 text-white font-bold uppercase tracking-wider hover:bg-red-500 disabled:opacity-50">
                {placing ? "Składanie…" : "Złóż zamówienie"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </RedfoxLayout>
  );
}
